/**
 * View helpers for the Billing page (TRA-1163). Pure functions over the
 * generated response types, so the customer words for each mode, the money
 * and period formatting, and the spend chart's geometry are pinned by tests,
 * and unpriced usage can never render as $0.
 */

import type {
  BillingAccrualLine,
  BillingInvoiceStatus,
  BillingOverviewResponseMode,
  BillingPaymentMethod,
  BillingSpendDay,
  HourlyPricingView,
  ModelRateView,
  TokenPricingView,
  TokenRateView,
} from '@/api/generated/model'
import type { BadgeProps } from '@/components/ui/badge'

export const MODE_LABELS: Record<BillingOverviewResponseMode, string> = {
  not_billed: 'Not billed',
  usage: 'Usage-based',
  gpu: 'Dedicated GPU with overage',
  canceled: 'Subscription canceled',
}

export const MODE_DESCRIPTIONS: Record<BillingOverviewResponseMode, string> = {
  not_billed: 'Nothing is metered or invoiced for this team.',
  usage: 'Tokens served through the gateway are metered daily and invoiced at the end of each cycle.',
  gpu: 'A flat monthly base covers your dedicated GPUs. Usage past them is metered as overage on the same invoice.',
  canceled: 'This subscription has ended. Nothing new is metered, and past invoices stay available below.',
}

export const INVOICE_STATUS_LABELS: Record<BillingInvoiceStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  paid: 'Paid',
  uncollectible: 'Uncollectible',
  void: 'Void',
}

export const INVOICE_STATUS_VARIANTS: Record<BillingInvoiceStatus, BadgeProps['variant']> = {
  draft: 'secondary',
  open: 'default',
  paid: 'success',
  uncollectible: 'destructive',
  void: 'secondary',
}

/**
 * Stripe reports integers in the currency's smallest unit. The account
 * invoices in usd, whose smallest unit is the cent.
 */
export function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100)
}

/** A gateway-priced figure (decimal USD string or number), to the cent. */
export function formatUsd(amount: number | string): string {
  return `$${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/** Rates carry up to four decimals ($0.0001 per 1M is a real price). */
function usdRate(amount: number): string {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`
}

/** "$1.00 / $0.50 / $3.60 per 1M" in input, cached, output order. */
export function rateLabel(rates: TokenRateView): string {
  return `${usdRate(rates.input_per_m)} / ${usdRate(rates.cached_per_m)} / ${usdRate(rates.output_per_m)} per 1M`
}

export function pricingLabel(pricing: TokenPricingView | HourlyPricingView): string {
  if (pricing.kind === 'hourly') {
    return `${usdRate(pricing.deployment_hour_usd)} per deployment hour`
  }
  return rateLabel(pricing.rates)
}

/** The badge on a rate the model inherits from the catalog (TRA-1282). */
export const LIST_PRICE_BADGE = 'list price'

/**
 * The catalog slug whose list price a model pays, as the line under its
 * rate: set only when the model is routed to another slug. A team's own
 * rate and a direct call to a catalog slug need no such line.
 */
export function listPriceSourceLabel(rate: Pick<ModelRateView, 'source' | 'catalog_slug'>): string | null {
  if (rate.source !== 'catalog' || rate.catalog_slug === null) return null
  return `${LIST_PRICE_BADGE} of ${rate.catalog_slug}`
}

/**
 * Spend for a window of traffic. Traffic the gateway could not price says
 * so; it never reads as a $0 bill.
 */
export function spendLabel(requestCount: number, costUsd: string | null): string {
  if (requestCount === 0) return 'no traffic'
  if (costUsd === null) return 'not priced'
  return formatUsd(costUsd)
}

/** Stripe periods are UTC day boundaries, so the day is read in UTC. */
export function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function formatPeriod(startIso: string, endIso: string): string {
  return `${formatDay(startIso)} to ${formatDay(endIso)}`
}

export function formatQuantity(quantity: number | null): string {
  return quantity === null ? '—' : quantity.toLocaleString('en-US')
}

const DAY_MS = 86_400_000

/**
 * Where `now` sits in a Stripe period: day N of M, one-based, clamped to
 * the period so a preview read a minute past the boundary still says the
 * last day rather than day M+1.
 */
export function cycleProgress(
  startIso: string,
  endIso: string,
  now: Date,
): { day: number; days: number } {
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  const days = Math.max(1, Math.round((end - start) / DAY_MS))
  const elapsed = Math.floor((now.getTime() - start) / DAY_MS) + 1
  return { day: Math.min(days, Math.max(1, elapsed)), days }
}

const CARD_BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
}

export function paymentMethodLabel(method: BillingPaymentMethod | null): string {
  if (method === null) return 'None on file'
  if (method.type === 'card' && method.last4 !== null) {
    const brand = CARD_BRANDS[method.brand ?? ''] ?? (method.brand ?? 'Card')
    const expiry =
      method.exp_month !== null && method.exp_year !== null
        ? `, expires ${String(method.exp_month).padStart(2, '0')}/${String(method.exp_year)}`
        : ''
    return `${brand} ending ${method.last4}${expiry}`
  }
  if (method.type === 'us_bank_account' && method.last4 !== null) {
    return `${method.bank_name ?? 'Bank account'} ending ${method.last4}`
  }
  return method.type.replace(/_/g, ' ')
}

/** A day with requests the gateway had no rate for: none priced, or only some. */
export function hasUnpricedTraffic(day: BillingSpendDay): boolean {
  return day.priced_request_count < day.request_count
}

/**
 * Priced dollars over the window, and how many days carried traffic the
 * gateway could not price. A mixed day contributes its priced part to the
 * total and still counts as unpriced, so the total is never presented as
 * the whole window's spend.
 */
export function spendTotals(days: BillingSpendDay[]): { priced: number; unpricedDays: number } {
  let priced = 0
  let unpricedDays = 0
  for (const day of days) {
    if (day.cost_usd !== null) priced += Number(day.cost_usd)
    if (hasUnpricedTraffic(day)) unpricedDays += 1
  }
  return { priced, unpricedDays }
}

export interface SpendPoint {
  /** UTC day, YYYY-MM-DD; the x key, so a marker can name a day exactly. */
  date: string
  /** Short axis label, "Sep 1". */
  label: string
  /** Priced dollars, or null so the line breaks instead of dipping to zero. */
  spend: number | null
  requests: number
  /** Some or all of the day's requests had no rate. */
  unpriced: boolean
}

export function dayLabel(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** The spend line's data: one point per UTC day of the gateway's grid. */
export function spendSeries(days: BillingSpendDay[]): SpendPoint[] {
  return days.map((day) => ({
    date: day.date,
    label: dayLabel(day.date),
    spend: day.cost_usd === null ? null : Number(day.cost_usd),
    requests: day.request_count,
    unpriced: hasUnpricedTraffic(day),
  }))
}

/** What a hovered day says about its spend. */
export function spendPointLabel(point: SpendPoint): string {
  if (point.spend === null) return point.requests === 0 ? 'no traffic' : 'not priced'
  return point.unpriced ? `${formatUsd(point.spend)} priced, some traffic not priced` : formatUsd(point.spend)
}

/**
 * Stripe's line description reads "1184035 × model · output tokens (at
 * $0.000001 / month)". The quantity has a column of its own, and for a
 * per-token rate the "/ month" (the price's billing interval) reads as the
 * wrong denominator, so both ends are dropped. The unit price is rendered
 * from the line's own figure instead.
 */
export function lineLabel(description: string | null): string {
  if (description === null) return '—'
  return description.replace(/\s*\(at \$[^)]*\)\s*$/, '').replace(/^\d[\d,]*\s×\s/, '')
}

/** A priced model's three meters, in the order a rate reads. */
export type TokenKind = 'fresh_input' | 'cached_input' | 'output'

const TOKEN_KINDS: TokenKind[] = ['fresh_input', 'cached_input', 'output']

export const TOKEN_KIND_LABELS: Record<TokenKind, string> = {
  fresh_input: 'Input',
  cached_input: 'Cached input',
  output: 'Output',
}

// The gateway names each metered product "<model> · <direction> tokens"
// (inference-gateway billing/core.ts), and Stripe prints that name on the
// line. Anything else on the invoice (a GPU base fee, overage minutes)
// does not match and stays a line of its own.
const TOKEN_LINE = /^(.+) · (fresh_input|cached_input|output) tokens$/

function isTokenKind(value: string): value is TokenKind {
  return TOKEN_KINDS.some((kind) => kind === value)
}

function tokenLineName(label: string): { model: string; kind: TokenKind } | null {
  const [, model, kind] = TOKEN_LINE.exec(label) ?? []
  if (model === undefined || kind === undefined || !isTokenKind(kind)) return null
  return { model, kind }
}

export interface CycleTokenLine {
  kind: TokenKind
  line: BillingAccrualLine
}

/** One model's metered lines this cycle and the sum Stripe will charge for them. */
export interface CycleModelGroup {
  type: 'model'
  model: string
  lines: CycleTokenLine[]
  subtotal: number
}

/** A line that is not a model's tokens, such as a GPU base fee. */
export interface CycleFlatLine {
  type: 'line'
  label: string
  line: BillingAccrualLine
}

export type CycleRow = CycleModelGroup | CycleFlatLine

/**
 * The cycle's lines as the table shows them. Stripe prints three lines for
 * every priced model, used or not, so a line with no quantity and no
 * amount is dropped. The rest group by model in Stripe's order, and a
 * model's lines read input, cached, output.
 */
export function cycleRows(lines: BillingAccrualLine[]): CycleRow[] {
  const rows: CycleRow[] = []
  const groups = new Map<string, CycleModelGroup>()
  for (const line of lines) {
    if (line.amount === 0 && line.quantity === 0) continue
    const label = lineLabel(line.description)
    const name = tokenLineName(label)
    if (name === null) {
      rows.push({ type: 'line', label, line })
      continue
    }
    let group = groups.get(name.model)
    if (group === undefined) {
      group = { type: 'model', model: name.model, lines: [], subtotal: 0 }
      groups.set(name.model, group)
      rows.push(group)
    }
    group.lines.push({ kind: name.kind, line })
    group.subtotal += line.amount
  }
  for (const group of groups.values()) {
    group.lines.sort((a, b) => TOKEN_KINDS.indexOf(a.kind) - TOKEN_KINDS.indexOf(b.kind))
  }
  return rows
}

/**
 * A line's unit price for a reader. Stripe reports it in the smallest
 * currency unit; anything below one cent can only be a per-token price
 * (nothing else here is priced that fine), so it reads per 1M tokens.
 * A whole-cent price is a flat item, like a GPU base fee.
 */
export function unitPriceLabel(unitAmountDecimal: string | null): string {
  if (unitAmountDecimal === null) return '—'
  const cents = Number(unitAmountDecimal)
  if (!Number.isFinite(cents)) return '—'
  if (cents < 1) return `${formatUsd((cents / 100) * 1_000_000)} per 1M tokens`
  return `${formatUsd(cents / 100)} each`
}
